import React, { useState, useEffect, Fragment } from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import {
  createTheme,
  ThemeProvider,
  Button,
  Box,
  IconButton,
  SvgIcon,
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import { Form, Col, Row, Container } from 'react-bootstrap';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from "yup";
import { MultiSelectInput, SelectInput, TextInput } from '../Helpers/Inputs';
import { HeaderStepper } from '../Quoters/Common/HeaderStepper/HeaderStepper';
import { getSessionStorageItem, setSessionStorageItem, SESSION_STORAGE_POLICY_REVIEW } from '../../sessionStorage';
import ExistingInsuranceIcon from '../../icons/existingInsurance.svg?react';
import BackNext from '../Quoters/Common/SubComponents/BackNext';
import InputTheme from '../Quoters/Common/Helpers/InputTheme';
import NumberFormatDollars from '../Helpers/Formatters/NumberFormatDollars';

import './ExistingInsurance.scss';

export default function ExistingInsurance() {
  const navigate = useNavigate();
  const location = useLocation();

  const prefill = location && location.search && location.search.toLowerCase().includes('prefill');
  const [formState, setFormState] = useState(ExistingInsurance.DefaultFormState(prefill, getSessionStorageItem(SESSION_STORAGE_POLICY_REVIEW)));

  const formMethods = useForm({
    resolver: yupResolver(ExistingInsurance.ValidationSchema),
    mode: 'all',
    reValidateMode: 'all',
    defaultValues: formState,
  });

  const { handleSubmit, trigger } = formMethods;
  const handleValueChange = (field, value) => {
    setFormState({ ...formState, [field]: value });
  };

  const handleCurrentInsuranceTypesChange = (field, value) => {
    handleValueChange(field, value);
    trigger(field);
  }

  const handleTermPolicyChange = (index, field, value) => {
    const newPoliciesFormState = formState.termPolicies;
    newPoliciesFormState[index][field] = value;
    setFormState({ ...formState, termPolicies: newPoliciesFormState });
  };
  const handlePermPolicyChange = (index, field, value) => {
    const newPoliciesFormState = formState.permPolicies;
    newPoliciesFormState[index][field] = value;
    setFormState({ ...formState, permPolicies: newPoliciesFormState });
  };

  const onSubmit = () => {
    setSessionStorageItem(SESSION_STORAGE_POLICY_REVIEW, formState);
    navigate('/policy-review/schedule');
  }

  useEffect(() => {
    if (formState.currentInsuranceTypes.includes('Term Life Insurance') && formState.termPolicies.length === 0) {
      onAddTermPolicy();
    }

    if (formState.currentInsuranceTypes.includes('Permanent Life Insurance') && formState.permPolicies.length === 0) {
      onAddPermPolicy();
    }

    if (!formState.currentInsuranceTypes.includes('Term Life Insurance') && formState.termPolicies.length > 0) {
      setFormState({ ...formState, termPolicies: [] });
    }

    if (!formState.currentInsuranceTypes.includes('Permanent Life Insurance') && formState.permPolicies.length > 0) {
      setFormState({ ...formState, permPolicies: [] });
    }
  }, [formState.currentInsuranceTypes]);

  useEffect(() => {
    window.scroll(0, 0);
  }, []);

  const onAddTermPolicy = () => {
    setFormState({ ...formState, termPolicies: [...formState.termPolicies, { coverageAmount: '', termLength: '' }] })
  };
  const onRemoveTermPolicy = (index) => {
    setFormState({ ...formState, termPolicies: [...formState.termPolicies.slice(0, index), ...formState.termPolicies.slice(index + 1)] })
  };

  const onAddPermPolicy = () => {
    setFormState({ ...formState, permPolicies: [...formState.permPolicies, { coverageAmount: '', coverageType: '' }] })
  };
  const onRemovePermPolicy = (index) => {
    setFormState({ ...formState, permPolicies: [...formState.permPolicies.slice(0, index), ...formState.permPolicies.slice(index + 1)] })
  };

  const termLengths = [
    { label: '10 Years', value: '10 Years' },
    { label: '15 Years', value: '15 Years' },
    { label: '20 Years', value: '20 Years' },
    { label: '25 Years', value: '25 Years' },
    { label: '30 Years', value: '30 Years' },
    { label: '35 Years', value: '35 Years' },
    { label: '40 Years', value: '40 Years' },
    { label: 'Other', value: 'Other' },
  ];

  const coverageTypes = [
    { label: 'Term Life', value: 'Term Life' },
    { label: 'Group', value: 'Group' },
    { label: 'Whole Life', value: 'Whole Life' },
    { label: 'Single Premium Whole Life', value: 'Single Premium Whole Life' },
    { label: 'Universal Life', value: 'Universal Life' },
    { label: 'Guaranteed Universal Life', value: 'Guaranteed Universal Life' },
    { label: 'Indexed Universal Life', value: 'Indexed Universal Life' },
    { label: 'Variable Universal Life', value: 'Variable Universal Life' },
    { label: 'Supplemental Health', value: 'Supplemental Health' },
    { label: 'Disability Income', value: 'Disability Income' },
    { label: 'Other', value: 'Other' },
  ];

  const termForms = formState.termPolicies.map((policy, key) => {
    return (
      <Fragment key={`termForm${key}`}>
        <Col className="title-control d-flex p-0 align-items-center">
          <span className="formSectionTitle">Term Life Insurance {key > 0 && <Fragment>{key + 1}</Fragment>}  (Optional)</span>
          {key > 0 && <Fragment><IconButton onClick={() => onRemoveTermPolicy(key)} size="large">{<Delete />}</IconButton></Fragment>}
        </Col>
        <div className='mb-3'>
          <TextInput
            name={`coverageAmountTerm${key}`}
            id={`coverageAmountTerm${key}`}
            value={policy.coverageAmount}
            onChange={(e) => handleTermPolicyChange(key, 'coverageAmount', e.target.value)}
            label="Coverage Amount"
            InputProps={{
              inputComponent: NumberFormatDollars,
            }}
          />
        </div>
        <div className='mb-3'>
          <SelectInput
            name="termLength"
            id={`termLengthTerm${key}`}
            value={policy.termLength}
            onChange={(e) => handleTermPolicyChange(key, 'termLength', e.target.value)}
            label="Term Length"
            selectOptions={termLengths}
          />
        </div>
      </Fragment>
    );
  });

  const permForms = formState.permPolicies.map((policy, key) => {
    return (
      <Fragment key={`permForm${key}`}>
        <Col className="title-control d-flex p-0 align-items-center">
          <span className="formSectionTitle">Permanent Life Insurance {key > 0 && <Fragment>{key + 1}</Fragment>}  (Optional)</span>
          {key > 0 &&
            <span>
              <IconButton onClick={() => onRemovePermPolicy(key)} size="large">{<Delete />}</IconButton>
            </span>
          }
        </Col>
        <div className='mb-3'>
          <TextInput
            name={`coverageAmountPerm${key}`}
            id={`coverageAmountPerm${key}`}
            value={policy.coverageAmount}
            onChange={(e) => handlePermPolicyChange(key, 'coverageAmount', e.target.value)}
            label="Coverage Amount"
            InputProps={{
              inputComponent: NumberFormatDollars,
            }}
          />
        </div>
        <div className='mb-3'>
          <SelectInput
            name="coverageType"
            id={`coverageType${key}`}
            value={policy.coverageType}
            onChange={(e) => handlePermPolicyChange(key, 'coverageType', e.target.value)}
            label="Coverage Type"
            selectOptions={coverageTypes}
          />
        </div>
      </Fragment>
    );
  });

  return (
    <ThemeProvider theme={(theme) => createTheme({
      ...theme,
      ...InputTheme(),
    })}>
      <HeaderStepper
        title="Let's Start Your Policy Review"
        stepName='Existing Insurance'
        step={2}
        totalSteps={3}
        hideContact={true}
      >
        <Container className="stepContainer">
          <FormProvider {...formMethods}>
            <Form onSubmit={handleSubmit(onSubmit)}>
              <Row className="justify-content-center">
                <Col lg={4} md={6} className='w-100'>
                  <Row className="justify-content-center pt-2">
                    <div className={`icon-container background-color-16`}>
                      <SvgIcon component={ExistingInsuranceIcon} color="primary" fontSize="large" />
                    </div>
                  </Row>
                  <Row className="justify-content-center mainTitle">
                    <h4>Existing Insurance Policy Details</h4>
                  </Row>
                  <div className="row-content current-insurance-types">
                    <Col>
                      <div className='mb-3'>
                        <MultiSelectInput
                          selectOptions={[
                            { label: "Term Life Insurance", value: "Term Life Insurance" },
                            { label: "Permanent Life Insurance", value: "Permanent Life Insurance" },
                          ]}
                          name="currentInsuranceTypes"
                          id="currentInsuranceTypes"
                          value={formState.currentInsuranceTypes}
                          onChange={(e) => handleCurrentInsuranceTypesChange('currentInsuranceTypes', e.target.value)}
                          label="Current Insurance (Select all that apply)"
                          renderLabelValue={true}
                        />
                      </div>

                      {formState.currentInsuranceTypes.includes('Term Life Insurance') &&
                        <div>
                          {termForms}

                          <Box sx={{
                            textAlign: "center"
                          }}>
                            <Button variant="text" onClick={onAddTermPolicy} className="addButton mb-3">Add another term policy</Button>
                          </Box>

                        </div>
                      }

                      {formState.currentInsuranceTypes.includes('Permanent Life Insurance') &&
                        <div>
                          {permForms}

                          <Box sx={{
                            textAlign: "center"
                          }}>
                            <Button variant="text" onClick={onAddPermPolicy} className="addButton mb-3">Add another permanent policy</Button>
                          </Box>

                        </div>
                      }

                    </Col>

                  </div>
                </Col>
              </Row>
              <Row className="nav-control">
                <Col>
                  <BackNext onBack={() => navigate('/policy-review')} continueLabel={{ text: "Next" }} />
                </Col>
              </Row>
            </Form>
          </FormProvider>
        </Container>
      </HeaderStepper>
    </ThemeProvider>
  );

}

ExistingInsurance.ValidationSchema = Yup.object().shape({
  currentInsuranceTypes: Yup.array()
    .min(1, 'Select at least one insurance type.')
    .required('Select at least one insurance type.'),
});

ExistingInsurance.DefaultFormState = (prefill = false, formState) => {
  return {
    currentInsuranceTypes: (formState && formState.currentInsuranceTypes) || [],
    termPolicies: (formState && formState.termPolicies) || [],
    permPolicies: (formState && formState.permPolicies) || [],
  };
};
